import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { setVendorPayment as setVendorPaymentAPI } from '../../../api/project-management.api'
import './scss/style.scss'
import Modal from '../../../components/modal/modal'
import FileUploader from '../../../components/file-uploader'
import idx from 'idx'
import DatePickerInput from "../../../components/inputs/date-picker";
import SelectInput from '../../../components/inputs/select'
import CustomInput from '../../../components/inputs/new-input'
import { addDays, formatISO } from 'date-fns';
import { ValidateField, ValidatorTypes } from '../../../util/validators'
import { sortByKeyAsc } from 'src/util/common'
import { history } from "../../../util/history";
import { VENDOR, VENDOR_ORDERS, CREATE_PURCHASE_ORDER, CREATE_WORK_ORDER } from "../../../constants/route-paths";
import { VENDOR_TAB, VENDOR_URL_PARAM } from "../../../constants/vendor.contant";
import DeleteIcon from '@mui/icons-material/Delete';

export default function ReceiveCredit({
  invoiceNumber,
  invoiceClaim,
  notes,
  approvedPayment,
  poId,
  isSubContractor,
  workType,
  savePurchaseOrder,
  successSnackBar,
  warningSnackBar,
  getProjectList,
  getPoVendors,
  getPOList,
  getVendorList,
  initialVendorType = 'Supplier',
  currentActiveTab,
  ...props
}) {

  const [fields, setFields] = useState({
    projectId: {
      name: 'projectId',
      label: 'Project',
      type: 'select',
      touched: false,
      error: false,
      valid: true,
      items: [],
      value: '',
      validation: [ValidatorTypes.REQUIRED]
    },
    vendorId: {
      name: 'vendorId',
      label: 'Vendor',
      type: 'select',
      touched: false,
      error: false,
      valid: true,
      items: [],
      value: '',
      validation: [ValidatorTypes.REQUIRED]
    },
    invoiceNumber: {
      name: 'invoiceNumber',
      label: 'Invoice Number',
      type: 'text',
      touched: false,
      error: false,
      valid: true,
      value: invoiceNumber || '',
      validation: [ValidatorTypes.REQUIRED, ValidatorTypes.MIN_TWO_CHARACTERS]
    },
    invoiceDate: {
      name: 'invoiceDate',
      label: 'Invoice Date',
      type: 'datepicker',
      touched: false,
      error: false,
      valid: true,
      value: '',
      validation: [ValidatorTypes.REQUIRED]
    },
    dueDate: {
      name: 'dueDate',
      label: 'Due Date',
      type: 'datepicker',
      touched: false,
      error: false,
      valid: true,
      value: '',
      validation: [ValidatorTypes.OPTIONAL]
    },
    creditAmount: {
      name: 'creditAmount',
      label: 'Credit Amount',
      type: 'number',
      touched: false,
      error: false,
      valid: true,
      value: '',
      validation: [ValidatorTypes.REQUIRED]
    }
  })
  const [submissionAttempted, setSubmissionAttempted] = useState(false)

  const [state, setState] = useState({
    workType,
    poId: null,
    poList: [],
    paymentTerms: 0,
    fileToUpload: []
  })

  useEffect(() => {
    getProject()
    fetchAllVendors();
  }, [])

  useEffect(() => {
    if (fields.invoiceDate.value && !fields.dueDate.value && state.paymentTerms) {
      setFields(prev => ({
        ...prev,
        dueDate: {
          ...prev.dueDate,
          value: formatISO(addDays(new Date(fields.invoiceDate.value), (Number(state.paymentTerms) - 1)))
        }
      }));
    }
  }, [fields.invoiceDate.value, state.paymentTerms]);

  const fetchAllVendors = async () => {
    try {
      const vendors = await getVendorList(initialVendorType, "ACTIVE", true);
      const vendorDropdown = (Array.isArray(vendors) ? vendors : []).map(vendor => ({
        key: vendor.businessName,
        value: vendor.id,
        type: vendor.type
      }));

      setFields(prev => ({
        ...prev,
        vendorId: {
          ...prev.vendorId,
          items: vendorDropdown
        }
      }));
    } catch (e) {
      console.error("Error loading vendors:", e);
    }
  };

  const inputChangeHandler = (e, fieldName = '') => {
    let name
    let value
    let type
    if (e.target.files) {
      name = e.target.name
      value = e.target.files[0]
    } else if (_.isObject(e)) {
      name = e.target.name
      value = e.target.value
      type = e.target.type
    }

    // Auto-add negative sign for credit amount if positive
    if (name === 'creditAmount' && value && !value.startsWith('-') && parseFloat(value) > 0) {
      value = `-${value}`
    }

    if (type === 'number' && name !== 'creditAmount') {
      value = isNaN(value) ? 0 : parseFloat(value)
    }

    // Validate field
    const { error, valid } = ValidateField({ value, validation: fields[name].validation })

    setFields(prev => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: value,
        touched: true,
        error: error,
        valid: valid,
      },
      ...((fieldName === 'invoiceDate' && state.paymentTerms) ? { dueDate: { ...prev.dueDate, value: formatISO(addDays(new Date(value), (Number(state.paymentTerms) - 1))) } } : {})
    }));
  }

  const submitForm = async () => {
    let _fields = _.cloneDeep(fields)
    setSubmissionAttempted(true)
    let isFormValid = true
    for (let key in _fields) {
      if (_fields[key]) {
        const { value, validation } = _fields[key]
        const { error, valid } = ValidateField({ value, validation })
        _fields[key].touched = true
        _fields[key].valid = valid
        _fields[key].error = error
        isFormValid = isFormValid && valid
      }
    }


    if (!isFormValid) {
      setFields(_fields)
      return
    }

    if (state.fileToUpload.length === 0) {
      warningSnackBar('Please upload at least one credit note document')
      return
    }

    if (parseFloat(_fields.creditAmount.value) >= 0) {
      warningSnackBar('Credit amount must be negative. Please enter a negative value or the system will automatically add a minus sign.')
      return
    }

    try {
      if (state.poId) {
        const poNumber = state.poId
        const formData = new FormData()
        formData.append('invoiceNumber', fields.invoiceNumber.value)
        formData.append('invoiceDate', fields.invoiceDate.value)
        formData.append('dueDate', fields.dueDate.value || '')
        const negAmount = parseFloat(fields.creditAmount.value) || 0
        formData.append('invoiceClaim', negAmount)
        formData.append('approvedPayment', negAmount)
        formData.append('notes', 'Credit Invoice')
        formData.append('invoiceStatus', 'PAID')
        formData.append('isSubContractor', false)
        formData.append('amountPaid', negAmount)
        formData.append('subTotal', 0)
        formData.append('gstPercentage', 0)
        formData.append('taxAmount', 0)
        formData.append('total', 0)
        const file0 = state.fileToUpload && state.fileToUpload[0]
        if (file0) formData.append('attachment', file0)
        await setVendorPaymentAPI(formData, poNumber)
        if (props.resetInvoiceData) props.resetInvoiceData()
        if (props.handleClose) props.handleClose()
        return
      }

      const poData = {
        vendorId: fields.vendorId.value,
        items: [],
        deliveryDate: new Date().toISOString(),
        quoteNo: '',
        projectId: fields.projectId.value,
        attachment: state.fileToUpload,
        isCreditInvoice: true,
        receiveCreditAmount: parseFloat(fields.creditAmount.value) || 0.00,
        invoiceNumber: fields.invoiceNumber.value
      }

      const result = await savePurchaseOrder(poData, '', 'Credit Invoice PO Created Successfully')
      if (result) {
        try {
          let poNumber = idx(result, _ => _.data.data.u_id) || idx(result, _ => _.data.u_id) || idx(result, _ => _.u_id) || ''
          if (!poNumber) {
            const { list: rawPOList } = await getPOList({ projectId: fields.projectId.value }).catch(e => null)
            if (Array.isArray(rawPOList)) {
              const creditPOs = rawPOList.filter(p => p.is_credit_invoice && idx(p, _ => _.vendor.id) === fields.vendorId.value)
              creditPOs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              poNumber = (creditPOs[0] && creditPOs[0].u_id) || ''
            }
          }
          if (!poNumber) throw new Error('Missing PO number for invoice creation')
          const formData = new FormData()
          formData.append('invoiceNumber', fields.invoiceNumber.value)
          formData.append('invoiceDate', fields.invoiceDate.value)
          formData.append('dueDate', fields.dueDate.value || '')
          const negAmount = parseFloat(fields.creditAmount.value) || 0
          formData.append('invoiceClaim', negAmount)
          formData.append('approvedPayment', negAmount)
          formData.append('notes', 'Credit Invoice')
          formData.append('invoiceStatus', 'PAID')
          formData.append('isSubContractor', false)
          formData.append('amountPaid', negAmount)
          formData.append('subTotal', 0)
          formData.append('gstPercentage', 0)
          formData.append('taxAmount', 0)
          formData.append('total', 0)
          const file0 = state.fileToUpload && state.fileToUpload[0]
          if (file0) formData.append('attachment', file0)
          await setVendorPaymentAPI(formData, poNumber)
          if (props.resetInvoiceData) props.resetInvoiceData()
        } catch (e) {
          const apiMsg = idx(e, _ => _.response.data.message) || 'Credit PO created, but failed to create credit invoice'
          console.log('setVendorPayment error:', idx(e, _ => _.response.data) || e)
          warningSnackBar(apiMsg)
        }
        if (props.handleClose) props.handleClose()
      }
    } catch (error) {
      warningSnackBar('Failed to create Credit Invoice PO')
    }
  }

  const getProject = async () => {
    try {
      let projects = await getProjectList({ type: 'PROJECT', isDropdown: true });
      let projectsList = []
      if (Array.isArray(projects)) {
        projects.forEach(({ name, id, type }) => { (type === 'PROJECT' || !type) && type !== 'TENDER' && projectsList.push({ key: name.trim(), value: id }) })
        projectsList = sortByKeyAsc(projectsList);
        setFields(prev => ({
          ...prev,
          projectId: {
            ...prev.projectId,
            items: projectsList
          }
        }));
      }
      // setState(prev => ({
      //   ...prev,
      //   projectsList
      // }));
    } catch (e) {
      setFields(prev => ({
        ...prev,
        projectId: {
          ...prev.projectId,
          items: []
        }
      }));
      // setState(prev => ({
      //   ...prev,
      //   projectsList: []
      // }));
      throw e;
    }
  }

  const changeProject = async (value) => {
    try {
      // Validate field
      const { error, valid } = ValidateField({ value: value, validation: fields.projectId.validation })
      setFields(prev => ({
        ...prev,
        projectId: {
          ...prev.projectId,
          value: value,
          error: error,
          valid: valid,
        }
      }));


      let vendorsList = await getPoVendors(value).catch(e => console.log(e));
      const vendors = [];

      Array.isArray(vendorsList) && vendorsList.forEach(vendor => {
        const vendorType = idx(vendor, _ => _.vendor?.type?.toLowerCase()) || '';
        const vendorId = idx(vendor, _ => _.vendor?.id) || '';
        const businessName = idx(vendor, _ => _.vendor?.business_name) || '';

        if (vendorType === initialVendorType.toLowerCase() && vendorId && businessName && !vendors.find(v => v.value === vendorId)) {
          vendors.push({ key: businessName, value: vendorId, type: vendorType });
        }
      });

      const { list: rawPOList } = await getPOList({ projectId: value }).catch(e => console.log(e));
      const POs = [];

      Array.isArray(rawPOList) && rawPOList.forEach(po => {
        const vendorType = idx(po, _ => _.vendor?.type);
        if (po.invoicestatus !== 'Fully Invoiced' && vendorType === initialVendorType) {
          POs.push({
            key: `${po.f_id || po.id}`,
            value: po.u_id,
            type: vendorType
          });
        }
      });


      setFields(prev => ({
        ...prev,
        vendorId: {
          ...prev.vendorId,
          items: vendors
        }
      }));

      setState(prev => ({
        ...prev,
        // vendorsList: vendors,
        poList: POs,
        vendorId: null,
        poId: null
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const changeVendor = async (value) => {
    try {
      // Validate field
      const { error, valid } = ValidateField({ value: value, validation: fields.vendorId.validation })
      setFields(prev => ({
        ...prev,
        vendorId: {
          ...prev.vendorId,
          value: value,
          error: error,
          valid: valid,
        }
      }));

      const projectId = fields.projectId.value;

      const { list: rawPOList } = await getPOList({
        projectId: projectId || null,
        vendorId: value
      }).catch(e => console.log(e));

      const POs = [];

      Array.isArray(rawPOList) && rawPOList.forEach(po => {
        const vendorType = idx(po, _ => _.vendor?.type);
        if (po.invoicestatus !== 'Fully Invoiced' && vendorType === initialVendorType) {
          POs.push({
            key: `${po.f_id || po.id}`,
            value: po.u_id,
            type: vendorType
          });
        }
      });

      setState(prev => ({
        ...prev,
        poList: POs,
        poId: null
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const changePO = (value) => {
    setState(prev => ({
      ...prev,
      poId: value,
    }));
  }

  const handleFileUpload = (files) => {
    setState(prev => ({
      ...prev,
      fileToUpload: [...prev.fileToUpload, ...Array.from(files)]
    }));
  }

  const removeFile = (index) => {
    setState(prev => ({
      ...prev,
      fileToUpload: prev.fileToUpload.filter((_, i) => i !== index)
    }));
  }

  const redirectToCreatePO = () => {
    if (isSubContractor) {
      // Redirect to create work order wrapper with return tab
      const returnTab = currentActiveTab || VENDOR_TAB.CONTRACTOR_INVOICES;
      history.navigate(`/${VENDOR}/${VENDOR_ORDERS}/${CREATE_WORK_ORDER}?${VENDOR_URL_PARAM.RETURN_TAB}=${returnTab}`);
    } else {
      // Redirect to create purchase order wrapper with return tab
      const returnTab = currentActiveTab || VENDOR_TAB.SUPPLIER_INVOICES;
      history.navigate(`/${VENDOR}/${VENDOR_ORDERS}/${CREATE_PURCHASE_ORDER}?${VENDOR_URL_PARAM.RETURN_TAB}=${returnTab}`);
    }
  }

  return (
    <>
      <Modal
        open={true}
        onClose={() => props.handleClose(false)}
        onClick={() => submitForm()}
        modalHeading={'Receive Credit Invoice'}
        modalSaveTxt={'Receive Invoice'}
        modalCloseTxt='Cancel'
        modalCloseBtn={() => props.handleClose(false)}
        showActionBtn={true}
        fullWidth={true}
        maxWidth={'xl'}
        cxlbtnclassName='cancelBtn'
        savebtnclassName='createBtn'
        primary
        data-cy="receive-credit-modal"
      >
        <div className="purchase-order-block mb-3">
          <div className="row">
            <div className="col-lg-4 col-md-4 mt-3">
              <SelectInput
                data-cy="project-dropdown"
                label='Project'
                {...fields.projectId}
                onChange={(e) => changeProject(e.target.value)}
              />
            </div>
            <div className="col-lg-4 col-md-4 mt-3">
              <SelectInput
                data-cy="vendor-dropdown"
                label='Vendor'
                {...fields.vendorId}
                onChange={(e) => changeVendor(e.target.value)}
              />
            </div>
            <div className="col-lg-2 col-md-4 mt-3">
              <SelectInput
                data-cy="po-dropdown"
                label={isSubContractor ? 'Work Order' : 'Purchase Order'}
                items={state.poList}
                value={state.poId}
                onChange={(e) => changePO(e.target.value)}
              />
            </div>
            <div className="col-lg-2 col-md-4 mt-4">
              <button
                type="button"
                className="btn-bg-brand-2 btn btn-primary w-100 credit-po-button"
                onClick={redirectToCreatePO}
              >
                {isSubContractor ? 'Create WO' : 'Create PO'}
              </button>
            </div>
            <div className="col-lg-4 col-md-4 mt-3">
              <CustomInput
                placeholder="Enter Invoice Number"
                {...fields.invoiceNumber}
                onChange={(e) => inputChangeHandler(e, 'invoiceNumber')}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <DatePickerInput
                {...fields.invoiceDate}
                onChange={(e, context) => {
                  inputChangeHandler({
                    target: { value: e, name: 'invoiceDate', type: 'date' },
                  }, "invoiceDate")
                }
                }
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <DatePickerInput
                {...fields.dueDate}
                onChange={(e, context) => {
                  inputChangeHandler({
                    target: { value: e, name: fields.dueDate.name, type: 'date' },
                  }, fields.dueDate.name)
                }
                }
              />
            </div>
            <div className="col-lg-4 col-md-4 mt-3">
              <CustomInput
                placeholder="Enter Credit Amount (will auto-add minus sign if positive)"
                {...fields.creditAmount}
                onChange={(e) => inputChangeHandler(e, 'creditAmount')}
              />
            </div>
            <div className="col-lg-4 col-md-6 mt-3 fileupload-section">
              <div className="file-section-part position-relative h-auto px-0">
                <div className="file-part">
                  <FileUploader
                    data-cy="credit-file"
                    title="Attach Credit Note Documents"
                    fileHandler={handleFileUpload}
                  />
                  <label htmlFor="#file-section"></label>
                </div>
                {submissionAttempted && state.fileToUpload.length === 0 && <small className="float-start text-danger">This Field is Required</small>}
                <input type="file" id="file-section" multiple></input>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 mt-3">
              <div className="form-group">
                <div className="input-block">
                  <label className="input-label">Uploaded Documents</label>
                  <p className="m-0 doc-info">
                    {Array.isArray(state.fileToUpload) && state.fileToUpload.map((file, index) => (
                      <span key={`new-${index}`} className="d-block mb-1">
                        <span>{file.name}</span>
                        <span onClick={() => removeFile(index)}><DeleteIcon /></span>
                      </span>
                    ))}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}
